import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import AnimatedLetter from '../components/AnimatedLetter';
import PreloaderProgressBar from '../components/PreloaderProgressBar';
import animationCache from '../utils/animationCache';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { exportAnimatedVideo } from '../utils/exportAnimatedVideo';
import './AnimatedTextEditor.css';

const AnimatedTextEditor = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [alphabets, setAlphabets] = useState([]);
  const [selectedAlphabet, setSelectedAlphabet] = useState(null);
  const [letters, setLetters] = useState({}); // Map lettre -> videoUrl (alphabet courant)
  const [lettersByAlphabet, setLettersByAlphabet] = useState({}); // { alphabetId: { letter: videoUrl } }
  const [letterThumbnails, setLetterThumbnails] = useState({}); // Map lettre -> thumbnailUrl (alphabet courant)
  const [thumbnailsByAlphabet, setThumbnailsByAlphabet] = useState({}); // { alphabetId: { letter: thumbnailUrl } }
  const [letterAnimatedPreviews, setLetterAnimatedPreviews] = useState({}); // Map lettre -> animatedPreviewUrl (alphabet courant)
  const [animatedPreviewsByAlphabet, setAnimatedPreviewsByAlphabet] = useState({}); // { alphabetId: { letter: animatedPreviewUrl } }
  const [alphabetPerCharIndex, setAlphabetPerCharIndex] = useState([]); // alphabetId par position dans le texte
  const [text, setText] = useState('');
  const [preloading, setPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [preloadLoaded, setPreloadLoaded] = useState(0);
  const [preloadTotal, setPreloadTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [replayKey, setReplayKey] = useState(0);
  const [triggeredPositions, setTriggeredPositions] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const [exportPhase, setExportPhase] = useState(null); // null | 'intro' | 'playing'
  const [letterSizeIndex, setLetterSizeIndex] = useState(0); // 0:100%, 1:75%, 2:50%, 3:25%
  const [cursorPosition, setCursorPosition] = useState({ line: 0, char: 0 }); // position virtuelle du curseur
  const textareaRef = useRef(null);
  const displayContentRef = useRef(null);

  // Fonction pour rejouer toutes les animations
  const handleReplay = () => {
    const nextTriggeredPositions = new Set();
    const lines = text.split('\n');
    lines.forEach((line, lineIndex) => {
      line.split('').forEach((char, charIndex) => {
        if (char.match(/[A-Za-z]/)) {
          nextTriggeredPositions.add(`${lineIndex}-${charIndex}`);
        }
      });
    });
    setTriggeredPositions(nextTriggeredPositions);
    setReplayKey(prev => prev + 1);

    // Après clic sur le bouton, redonner le focus au textarea pour permettre de retaper immédiatement.
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.focus();
      const caretPos = textarea.value.length;
      textarea.setSelectionRange(caretPos, caretPos);
    }
  };

  // Export vidéo (admin uniquement) : capture l'onglet, phase intro (1s première frame) puis playing (7s animation)
  const handleExport = async () => {
    if (!text.trim()) return;
    setExporting(true);
    try {
      if (displayContentRef.current) displayContentRef.current.scrollTop = 0;
      await exportAnimatedVideo(
        displayContentRef,
        selectedAlphabet?.couleur_fond || '#FFFFFF',
        7000,
        (phase) => {
          if (phase === 'intro') {
            setReplayKey(prev => prev + 1);
            setExportPhase('intro');
          } else if (phase === 'playing') {
            setExportPhase('playing');
          }
        }
      );
    } catch (err) {
      console.error('Erreur export vidéo:', err);
      alert(err.message || 'Erreur lors de l\'export.');
    } finally {
      setExporting(false);
      setExportPhase(null);
    }
  };

  // Charger les alphabets disponibles
  useEffect(() => {
    const loadAlphabets = async () => {
      try {
        setLoading(true);
        const response = await api.get('/animated-letters/alphabets/');
        const alphabetsList = response.data.results || response.data;
        setAlphabets(alphabetsList);
        
        // Sélectionner le premier alphabet par défaut
        if (alphabetsList.length > 0 && !selectedAlphabet) {
          setSelectedAlphabet(alphabetsList[0]);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des alphabets:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAlphabets();
  }, []);

  // Charger les lettres de l'alphabet sélectionné
  useEffect(() => {
    const loadLetters = async () => {
      if (!selectedAlphabet) return;

      try {
        const response = await api.get(`/animated-letters/letters/?alphabet=${selectedAlphabet.id}`);
        const lettersList = response.data.results || response.data;
        
        // Créer une map lettre -> videoUrl
        const lettersMap = {};
        const thumbnailsMap = {};
        const previewsMap = {};
        lettersList.forEach(letter => {
          const upperLetter = letter.lettre.toUpperCase();
          lettersMap[upperLetter] = letter.video_url;
          thumbnailsMap[upperLetter] = letter.thumbnail_url || null;
          previewsMap[upperLetter] = letter.animated_preview_url || null;
        });
        
        setLetters(lettersMap);
        setLetterThumbnails(thumbnailsMap);
        setLetterAnimatedPreviews(previewsMap);
        setLettersByAlphabet(prev => ({ ...prev, [selectedAlphabet.id]: lettersMap }));
        setThumbnailsByAlphabet(prev => ({ ...prev, [selectedAlphabet.id]: thumbnailsMap }));
        setAnimatedPreviewsByAlphabet(prev => ({ ...prev, [selectedAlphabet.id]: previewsMap }));

        // Précharger en mémoire toutes les vidéos de l'alphabet sélectionné
        await preloadAllLetters(lettersMap);
      } catch (error) {
        console.error('Erreur lors du chargement des lettres:', error);
      }
    };

    loadLetters();
  }, [selectedAlphabet]);

  // Charger les lettres des alphabets utilisés dans le texte (au cas où on a tapé avant la fin du chargement)
  useEffect(() => {
    const alphabetIds = [...new Set(alphabetPerCharIndex.filter(Boolean))];
    const toLoad = alphabetIds.filter(id => !lettersByAlphabet[id]);
    if (toLoad.length === 0) return;

    toLoad.forEach(async (alphabetId) => {
      try {
        const response = await api.get(`/animated-letters/letters/?alphabet=${alphabetId}`);
        const lettersList = response.data.results || response.data;
        const lettersMap = {};
        const thumbnailsMap = {};
        const previewsMap = {};
        lettersList.forEach(letter => {
          const upperLetter = letter.lettre.toUpperCase();
          lettersMap[upperLetter] = letter.video_url;
          thumbnailsMap[upperLetter] = letter.thumbnail_url || null;
          previewsMap[upperLetter] = letter.animated_preview_url || null;
        });
        setLettersByAlphabet(prev => ({ ...prev, [alphabetId]: lettersMap }));
        setThumbnailsByAlphabet(prev => ({ ...prev, [alphabetId]: thumbnailsMap }));
        setAnimatedPreviewsByAlphabet(prev => ({ ...prev, [alphabetId]: previewsMap }));
      } catch (error) {
        console.error(`Erreur chargement lettres alphabet ${alphabetId}:`, error);
      }
    });
  }, [alphabetPerCharIndex.join(',')]);

  // Précharger toutes les lettres de l'alphabet sélectionné
  const preloadAllLetters = async (lettersMap) => {
    if (!selectedAlphabet || !lettersMap) return;

    setPreloading(true);
    setPreloadLoaded(0);

    // Toutes les lettres disponibles pour cet alphabet
    const availableLetters = Object.keys(lettersMap);
    setPreloadTotal(availableLetters.length);

    // Créer un map pour les URLs
    const videoUrls = {};
    availableLetters.forEach(letter => {
      videoUrls[letter] = lettersMap[letter];
    });

    // Précharger avec callback de progression
    const handleProgress = (loaded, total, percentage) => {
      setPreloadLoaded(loaded);
      setPreloadTotal(total);
      setPreloadProgress(percentage);
    };

    try {
      await animationCache.preloadLetters(
        availableLetters,
        selectedAlphabet.id,
        videoUrls,
        handleProgress
      );
    } catch (error) {
      console.error('Erreur lors du préchargement:', error);
    } finally {
      setPreloading(false);
    }
  };

  // Gérer le changement d'alphabet
  const handleAlphabetChange = (e) => {
    const alphabetId = parseInt(e.target.value);
    const alphabet = alphabets.find(a => a.id === alphabetId);
    
    if (alphabet) {
      setSelectedAlphabet(alphabet);
      // Ne pas réinitialiser triggeredPositions ici :
      // on veut conserver l'état final des lettres déjà déclenchées
      // même après changement d'alphabet.
      animationCache.switchAlphabet(alphabet.id);
      // Limiter le cache : évincer les alphabets non utilisés au-delà de 3
      const alphabetsInUse = new Set([
        alphabet.id,
        ...alphabetPerCharIndex.filter(Boolean)
      ]);
      if (alphabetsInUse.size > 3) {
        const toEvict = [...alphabetsInUse].filter(id => id !== alphabet.id);
        const byCount = toEvict.map(id => ({
          id,
          count: alphabetPerCharIndex.filter(a => a === id).length
        })).sort((a, b) => a.count - b.count);
        if (byCount.length > 0) {
          animationCache.clearCache(byCount[0].id);
        }
      }
    }
  };

  // Gérer la saisie de texte
  const handleTextChange = (e) => {
    const newText = e.target.value;
    const selectionStart = e.target.selectionStart;
    const delta = newText.length - text.length;
    
    if (delta < 0) {
      setTriggeredPositions(new Set());
      const deleteIndex = (() => {
        for (let i = 0; i <= text.length; i++) {
          if (i === newText.length || newText[i] !== text[i]) return i;
        }
        return newText.length;
      })();
      setAlphabetPerCharIndex(prev => {
        if (prev.length !== text.length) {
          return new Array(newText.length).fill(selectedAlphabet?.id || null);
        }
        const next = [...prev];
        next.splice(deleteIndex, 1);
        return next;
      });
    } else if (delta === 1 && selectedAlphabet) {
      // Ajout d'un caractère
      const insertIndex = (() => {
        for (let i = 0; i <= text.length; i++) {
          if (i === text.length || newText[i] !== text[i]) return i;
        }
        return text.length;
      })();
      setAlphabetPerCharIndex(prev => {
        const next = [...prev];
        next.splice(insertIndex, 0, selectedAlphabet.id);
        return next;
      });
    } else if (delta !== 0) {
      // Coller plusieurs caractères : reconstruire avec l'alphabet courant
      setAlphabetPerCharIndex(new Array(newText.length).fill(selectedAlphabet?.id || null));
    }
    
    setText(newText);
    // Mettre à jour la position du curseur virtuel après la modification
    setCursorPosition(computeCursorPosition(newText, selectionStart));
  };

  // Frappe d'une lettre : si une occurrence antérieure (non déclenchée) existe, lancer sa vidéo sans ajouter de caractère
  const handleKeyDown = (e) => {
    const key = e.key;
    if (key.length !== 1 || !key.match(/[A-Za-z]/)) return;
    
    const upperKey = key.toUpperCase();
    // Utiliser l'alphabet sélectionné pour la touche pressée (lettre à ajouter ou à déclencher)
    const lettersForKey = lettersByAlphabet[selectedAlphabet?.id] || letters;
    if (!selectedAlphabet || !lettersForKey?.[upperKey]) return;
    
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const cursorPos = textarea.selectionStart;
    // Mettre à jour la position du curseur virtuel au moment de la frappe
    setCursorPosition(computeCursorPosition(text, cursorPos));
    
    // Chercher la 1ère occurrence de cette lettre avant le curseur, non encore déclenchée
    const lines = text.split('\n');
    let pos = 0;
    let firstUntriggered = null;
    
    for (let li = 0; li < lines.length && firstUntriggered === null; li++) {
      for (let ci = 0; ci < lines[li].length; ci++) {
        if (pos >= cursorPos) break;
        const char = lines[li][ci];
        const alphabetId = alphabetPerCharIndex[pos] ?? selectedAlphabet?.id;
        // Ne déclencher que si l'occurrence antérieure est du MÊME alphabet (évite 2 frappes pour ajouter)
        if (alphabetId !== selectedAlphabet?.id) {
          pos++;
          continue;
        }
        const lettersForChar = lettersByAlphabet[alphabetId] || letters;
        if (char.toUpperCase() === upperKey && lettersForChar?.[upperKey]) {
          const positionKey = `${li}-${ci}`;
          if (!triggeredPositions.has(positionKey)) {
            firstUntriggered = positionKey;
            break;
          }
        }
        pos++;
      }
    }
    
    if (firstUntriggered) {
      e.preventDefault();
      setTriggeredPositions(prev => new Set(prev).add(firstUntriggered));
    }
  };

  // Gérer le scroll pour synchroniser textarea et zone d'affichage
  const handleScroll = (e) => {
    const displayContent = document.querySelector('.editor-display-content');
    if (displayContent) {
      // Calculer le ratio de scroll et l'appliquer à la zone d'affichage
      const textarea = e.target;
      const textareaScrollRatio = textarea.scrollTop / (textarea.scrollHeight - textarea.clientHeight);
      const displayMaxScroll = displayContent.scrollHeight - displayContent.clientHeight;
      displayContent.scrollTop = textareaScrollRatio * displayMaxScroll;
    }
  };

  // Gérer le scroll de la molette sur le conteneur
  const handleWheel = (e) => {
    const displayContent = document.querySelector('.editor-display-content');
    const textarea = textareaRef.current;
    if (displayContent && textarea) {
      e.preventDefault();
      const currentScroll = displayContent.scrollTop;
      const newScroll = currentScroll + e.deltaY;
      displayContent.scrollTop = Math.max(0, Math.min(newScroll, displayContent.scrollHeight - displayContent.clientHeight));
      // Synchroniser le textarea
      const displayScrollRatio = displayContent.scrollTop / (displayContent.scrollHeight - displayContent.clientHeight);
      const textareaMaxScroll = textarea.scrollHeight - textarea.clientHeight;
      textarea.scrollTop = displayScrollRatio * textareaMaxScroll;
    }
  };

  const sizeScales = [1, 0.75, 0.5, 0.25];
  const currentSizeScale = sizeScales[letterSizeIndex] || 1;

  const computeCursorPosition = (value, selectionStart) => {
    const safeSelection = Math.max(0, Math.min(selectionStart ?? 0, value.length));
    const beforeCursor = value.slice(0, safeSelection);
    const linesUntilCursor = beforeCursor.split('\n');
    const line = linesUntilCursor.length - 1;
    const char = linesUntilCursor[linesUntilCursor.length - 1].length;
    return { line, char };
  };

  // Rendre le texte avec les animations - chaque lettre utilise l'alphabet avec lequel elle a été saisie
  const renderAnimatedText = () => {
    if (!selectedAlphabet) {
      return <div className="no-alphabet-message">{t('animatedEditor.selectAlphabet')}</div>;
    }

    if (!text.trim()) {
      return <div className="no-alphabet-message">{t('animatedEditor.typeText')}</div>;
    }

    const lines = text.split('\n');
    let flatIndex = 0;
    
    return lines.map((line, lineIndex) => {
      let previousAnimatedCount = 0;
      
      const elements = line.split('').map((char, charIndex) => {
        const currentFlatIndex = flatIndex++;
        const alphabetId = alphabetPerCharIndex[currentFlatIndex] ?? selectedAlphabet.id;
        // Ne jamais utiliser letters (alphabet courant) pour un autre alphabet : chaque lettre utilise son alphabet d'origine
        const lettersForChar = alphabetId === selectedAlphabet?.id
          ? (lettersByAlphabet[alphabetId] || letters)
          : lettersByAlphabet[alphabetId];
        const thumbnailsForChar = alphabetId === selectedAlphabet?.id
          ? (thumbnailsByAlphabet[alphabetId] || letterThumbnails)
          : thumbnailsByAlphabet[alphabetId];
        const previewsForChar = alphabetId === selectedAlphabet?.id
          ? (animatedPreviewsByAlphabet[alphabetId] || letterAnimatedPreviews)
          : animatedPreviewsByAlphabet[alphabetId];
        const alphabetObj = alphabets.find(a => a.id === alphabetId) || selectedAlphabet;
        const upperChar = char.toUpperCase();
        
        if (char.match(/[A-Za-z]/) && lettersForChar?.[upperChar]) {
          const isFirstLetter = previousAnimatedCount === 0;
          previousAnimatedCount++;
          
          const positionKey = `${lineIndex}-${charIndex}`;
          const shouldPlayVideo = triggeredPositions.has(positionKey);
          
          return (
            <AnimatedLetter
              key={`${lineIndex}-${charIndex}`}
              letter={char}
              alphabetId={alphabetId}
              videoUrl={lettersForChar[upperChar]}
              fallbackImageUrl={thumbnailsForChar?.[upperChar] || null}
              animatedPreviewUrl={previewsForChar?.[upperChar] || null}
              superpositionPourcentage={alphabetObj?.superposition_pourcentage ?? 0}
              superpositionType={alphabetObj?.superposition_type ?? 'normal'}
              isFirstLetter={isFirstLetter}
              replayKey={replayKey}
              shouldPlayVideo={shouldPlayVideo}
              exportPhase={exportPhase}
              sizeScale={currentSizeScale}
            />
          );
        }
        
        if (char === ' ') {
          return (
            <span key={`${lineIndex}-${charIndex}`} className="animated-text-space">
              {' '}
            </span>
          );
        }
        
        return null;
      }).filter(Boolean);
      
      // Afficher la ligne même si elle ne contient que des espaces ou est vide
      // Cela permet de préserver les retours à la ligne
      return (
        <div key={lineIndex} className="animated-text-line">
          {elements.length > 0 ? elements : <br />}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="animated-text-editor">
        <Navbar />
        <div className="loading-message">{t('animatedEditor.loading')}</div>
      </div>
    );
  }

  return (
    <div className="animated-text-editor">
      <Navbar />
      
      {preloading && (
        <PreloaderProgressBar
          progress={preloadProgress}
          loaded={preloadLoaded}
          total={preloadTotal}
        />
      )}

      <div className="editor-content">
        <div className="editor-actions">
          <h1 className="editor-title">{t('animatedEditor.title')}</h1>
          
          {alphabets.length > 0 && (
            <div className="alphabet-selector-container">
              <label htmlFor="alphabet-select">{t('animatedEditor.alphabet')}:</label>
              <select
                id="alphabet-select"
                value={selectedAlphabet?.id || ''}
                onChange={handleAlphabetChange}
                className="alphabet-selector"
                disabled={preloading}
              >
                {alphabets.map((alphabet) => (
                  <option key={alphabet.id} value={alphabet.id}>
                    {alphabet.nom} ({alphabet.nombre_lettres || 0} {t('animatedEditor.letters')})
                  </option>
                ))}
              </select>

              <div className="letter-size-control">
                <div className="letter-size-header">
                  <span className="letter-size-title">
                    Taille des lettres
                  </span>
                  <span className="letter-size-value">
                    {(() => {
                      switch (letterSizeIndex) {
                        case 0:
                          return '100%';
                        case 1:
                          return '75%';
                        case 2:
                          return '50%';
                        case 3:
                          return '25%';
                        default:
                          return '100%';
                      }
                    })()}
                  </span>
                </div>
                <input
                  id="letter-size-range"
                  type="range"
                  min="0"
                  max="3"
                  step="1"
                  value={letterSizeIndex}
                  onChange={(e) => setLetterSizeIndex(parseInt(e.target.value, 10))}
                />
              </div>
            </div>
          )}
          <button
            onClick={handleReplay}
            className="replay-button"
            disabled={!selectedAlphabet || !text.trim() || preloading}
            title={t('animatedEditor.play')}
          >
            {t('animatedEditor.play')}
          </button>
          {user?.is_staff && (
            <button
              onClick={handleExport}
              className="replay-button export-button"
              disabled={!selectedAlphabet || !text.trim() || preloading || exporting}
              title={t('animatedEditor.export') || 'Exporter en vidéo - Sélectionnez cet onglet puis Partager'}
            >
              {exporting ? '…' : (t('animatedEditor.export') || 'Export')}
            </button>
          )}
        </div>

        <div className="editor-section-single">
          <div className="editor-display-container" onWheel={handleWheel}>
            <div 
              ref={displayContentRef}
              className="editor-display-content"
              style={{
                backgroundColor: selectedAlphabet?.couleur_fond || '#FFFFFF'
              }}
            >
              <div
                className="alphabet-baseline"
                style={{
                  top: `${200 * currentSizeScale * (cursorPosition.line + 1)}px`,
                  // Curseur = 50% de la largeur d'une lettre, centré
                  left: `${(200 * cursorPosition.char + 50) * currentSizeScale}px`,
                  width: `${100 * currentSizeScale}px`
                }}
              />
              {renderAnimatedText()}
            </div>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onScroll={handleScroll}
              placeholder={t('animatedEditor.placeholder')}
              className="text-input-overlay"
              disabled={preloading || !selectedAlphabet}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedTextEditor;
